/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import { DRAFT_VIEW, DraftManagementContext } from '../../pages/draftManagement/useDraftManagement';
import { shortFormatDate } from '../../shared/utils/dateUtils';
import {
  getFirstApplicantName,
  getTableFilterColumnAccessors,
} from '../../shared/submission/submissionPropertyAccessor';
import { usePartiesService } from '../../shared/submission/usePartiesService';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../../redux/enums/ConfigResourceProps';

const useStyles = makeStyles(theme => ({
  container: {
    marginBottom: theme.spacing(1),
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  divider: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  propertyName: {
    height: '1.5rem',
  },
  title: {
    fontSize: '1rem',
    fontWeight: '600',
  },
  propertyValue: {
    height: '1.5rem',
    fontWeight: 'bold',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: '1rem',
    },
  },
}));

export default function SimpleSubmissionTable({ submissions, view }) {
  const { data } = useContext(DraftManagementContext);
  const { t } = useTranslation();
  const classes = useStyles();
  const { isPartyCompany } = usePartiesService();

  const frontendCustomisation = useConfiguration(ConfigResourceProps.FRONTEND_CUSTOMISATION);
  const frontendCustomisationDateFormat = frontendCustomisation.resource?.dateFormat;
  return view === DRAFT_VIEW.GRID ? (
    submissions?.map(row => (
      <Box key={row.id} className={classes.container}>
        <Divider className={classes.divider} />
        <Grid container>
          <Grid container item xs={4} className={classes.propertyName}>
            {t('basic-filing.user-reference.sort')}
          </Grid>
          <Grid container item xs={8} className={classes.propertyValue}>
            <Typography variant="body1" component="p" gutterBottom noWrap className={classes.title}>
              {row[getTableFilterColumnAccessors()[0]]}
            </Typography>
          </Grid>
          <Grid container item xs={4} className={classes.propertyName}>
            {t('parties.applicant')}
          </Grid>
          <Grid container item xs={8} className={classes.propertyValue}>
            <Typography variant="body1" component="p" gutterBottom noWrap className={classes.title}>
              {getFirstApplicantName(
                data?.filter(d => d.id === row.id),
                isPartyCompany,
                t,
              )}
            </Typography>
          </Grid>
          <Grid container item xs={4} className={classes.propertyName}>
            {t('Title')}
          </Grid>
          <Grid container item xs={8} className={classes.propertyValue}>
            <Typography variant="body1" component="p" gutterBottom noWrap className={classes.title}>
              {row[getTableFilterColumnAccessors()[1]]}
            </Typography>
          </Grid>
          <Grid container item xs={4} className={classes.propertyName}>
            {t('basic-filing.last-modified.sort')}
          </Grid>
          <Grid container item xs={8} className={classes.propertyValue}>
            {shortFormatDate(new Date(Date.parse(row.modifiedDate)), frontendCustomisationDateFormat)}
          </Grid>
        </Grid>
      </Box>
    ))
  ) : (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell align="left">{t('basic-filing.user-reference')}</TableCell>
          <TableCell align="left">{t('parties.applicant')}</TableCell>
          <TableCell align="left">{t('basic-filing.title-of-invention')}</TableCell>
          <TableCell align="left">{t('basic-filing.last-modified.sort')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {submissions?.map(row => (
          <TableRow key={row.id} id={row.id}>
            <TableCell align="left">{row[getTableFilterColumnAccessors()[0]]}</TableCell>
            <TableCell align="left">
              {getFirstApplicantName(
                data?.filter(d => d.id === row.id),
                isPartyCompany,
                t,
              )}
            </TableCell>
            <TableCell align="left">{row[getTableFilterColumnAccessors()[1]]}</TableCell>
            <TableCell align="left">{shortFormatDate(new Date(Date.parse(row.modifiedDate)))}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
